/*
 * <oilogic-bundle-hero-overlay> — injects the three theme-setting product
 * images over the app-rendered bag (.bundle-hero__image) once it appears, and
 * gives them the hero float + cursor-repel effect. A single shared Motion
 * depth and Float toggle (data-depth / data-float, from theme settings) govern
 * all three. The idle bob is CSS; the cursor repel is driven here, mouse-only
 * and skipped under prefers-reduced-motion. The layers stay full-bleed, so
 * each one's repel centre comes from its --repel-anchor-{x,y} custom property
 * (its background-position) rather than its bounding box.
 */
(() => {
  const REPEL_RADIUS = 180;
  const REPEL_PER_DEPTH = 1.6;
  const LERP = 0.1;
  const EPSILON = 0.1;

  class OilogicBundleHeroOverlay extends HTMLElement {
    connectedCallback() {
      this.depth = Number(this.dataset.depth);
      if (!Number.isFinite(this.depth)) this.depth = 5;
      this.float = this.dataset.float !== 'false';

      this.tryApply();
      if (this.applied) return;

      this.observer = new MutationObserver(() => this.tryApply());
      this.observer.observe(document.body, { childList: true, subtree: true });

      // Loaded on every page (the app renders the hero outside our templates,
      // so there's no template to scope to). Give up observing after 10s on
      // pages that never render the hero.
      this.giveUpTimer = setTimeout(() => this.observer?.disconnect(), 10000);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.giveUpTimer);
      this.repelAbort?.abort();
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    tryApply() {
      const container = document.querySelector('.bundle-hero__image');
      if (!container || container.dataset.oilogicOverlayApplied) return;

      container.dataset.oilogicOverlayApplied = 'true';
      this.container = container;

      this.layers = ['1', '2', '3'].map((index, i) => {
        const layer = document.createElement('div');
        layer.className = `bundle-hero__product-image bundle-hero__product-image--${index}`;
        if (!this.float || this.depth === 0) {
          layer.classList.add('bundle-hero__product-image--no-float');
        }
        layer.style.setProperty('--bundle-depth', String(this.depth));
        layer.style.setProperty('--bundle-bob-dur', `${3600 + (i % 4) * 350}ms`);
        layer.style.setProperty('--bundle-bob-delay', `-${i * 700}ms`);
        container.appendChild(layer);
        return { el: layer, dx: 0, dy: 0, tx: 0, ty: 0 };
      });

      this.applied = true;
      this.observer?.disconnect();
      this.initRepel();
    }

    initRepel() {
      // depth 0 = fully static; repel is mouse-only and respects reduced motion.
      if (this.depth <= 0) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      const zone = this.container.closest('.bundle-hero') || this.container;
      this.repelAbort = new AbortController();
      const { signal } = this.repelAbort;

      zone.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          this.pointer = { x: event.clientX, y: event.clientY };
          this.startRepel();
        },
        { signal }
      );

      zone.addEventListener(
        'pointerleave',
        () => {
          this.pointer = null;
          this.startRepel();
        },
        { signal }
      );
    }

    startRepel() {
      if (!this.raf) this.raf = requestAnimationFrame(() => this.tickRepel());
    }

    tickRepel() {
      this.raf = 0;
      // The container is never transformed by the repel (that lives on the
      // child layers), so its rect is a stable reference for the anchors.
      const rect = this.container.getBoundingClientRect();
      let settling = false;

      for (const item of this.layers) {
        if (this.pointer) {
          const style = getComputedStyle(item.el);
          const ax = parseFloat(style.getPropertyValue('--repel-anchor-x')) || 50;
          const ay = parseFloat(style.getPropertyValue('--repel-anchor-y')) || 50;
          const cx = rect.left + (ax / 100) * rect.width;
          const cy = rect.top + (ay / 100) * rect.height;
          const vx = cx - this.pointer.x;
          const vy = cy - this.pointer.y;
          const distance = Math.hypot(vx, vy) || 1;
          const falloff = Math.max(0, 1 - distance / REPEL_RADIUS);
          const strength = REPEL_PER_DEPTH * this.depth * falloff;
          item.tx = (vx / distance) * strength;
          item.ty = (vy / distance) * strength;
        } else {
          item.tx = 0;
          item.ty = 0;
        }

        item.dx += (item.tx - item.dx) * LERP;
        item.dy += (item.ty - item.dy) * LERP;

        if (Math.abs(item.tx - item.dx) > EPSILON || Math.abs(item.ty - item.dy) > EPSILON) {
          settling = true;
        } else {
          item.dx = item.tx;
          item.dy = item.ty;
        }

        item.el.style.transform = `translate3d(${item.dx}px, ${item.dy}px, 0)`;
      }

      // Keep animating only while something is still gliding — a parked cursor
      // costs zero frames; the next pointermove restarts the loop.
      if (settling) this.startRepel();
    }
  }

  if (!customElements.get('oilogic-bundle-hero-overlay')) {
    customElements.define('oilogic-bundle-hero-overlay', OilogicBundleHeroOverlay);
  }
})();
